import { DocumentNode } from "graphql";
import { useLazyQuery } from "@apollo/client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

type CompanyLookupItem = {
  id: string;
  name: string;
};

type CompanyLookupResponse<T extends CompanyLookupItem> = {
  getCompanies?: {
    data?: T[] | null;
    paginatorInfo?: {
      currentPage: number;
      hasMorePages: boolean;
    } | null;
  } | null;
};

type CompanyLookupVariables = {
  first: number;
  page: number;
  search: string;
};

type CompanyLookupOptions = {
  pageSize?: number;
  preserveItemsOnShortSearch?: boolean;
};

const PAGE_SIZE = 50;
const MINIMUM_SEARCH_LENGTH = 2;

export default function usePaginatedCompanyLookup<T extends CompanyLookupItem>(
  query: DocumentNode,
  selectedIds: string[] = [],
  enabled = true,
  pageSizeOrOptions: number | CompanyLookupOptions = PAGE_SIZE
) {
  const { pageSize = PAGE_SIZE, preserveItemsOnShortSearch = false } =
    typeof pageSizeOrOptions === "number" ? { pageSize: pageSizeOrOptions } : pageSizeOrOptions;
  const [execute, { loading, error }] = useLazyQuery<
    CompanyLookupResponse<T>,
    CompanyLookupVariables
  >(query, { fetchPolicy: "network-only" });
  const [search, setSearch] = useState("");
  const [items, setItems] = useState<T[]>([]);
  const [cache, setCache] = useState<Record<string, T>>({});
  const [paginator, setPaginator] = useState({ currentPage: 0, hasMorePages: false });
  const [activeSearch, setActiveSearch] = useState("");
  const requestVersion = useRef(0);
  const requestedIds = useRef(new Set<string>());
  const skipNextSearchEffect = useRef(false);
  const selectedIdsKey = selectedIds.join(",");

  const storeItems = useCallback((nextItems: T[]) => {
    setCache((previous) => ({
      ...previous,
      ...Object.fromEntries(nextItems.map((item) => [item.id, item])),
    }));
  }, []);

  const loadPage = useCallback(
    async (nextSearch: string, page: number, append: boolean) => {
      const version = ++requestVersion.current;
      let result;
      try {
        result = await execute({
          variables: { first: pageSize, page, search: nextSearch },
        });
      } catch {
        return;
      }

      if (version !== requestVersion.current) return;

      const response = result.data?.getCompanies;
      const nextItems = response?.data ?? [];
      storeItems(nextItems);
      setActiveSearch(nextSearch);
      setItems((previous) => {
        if (!append) return nextItems;
        const byId = new Map(previous.map((item) => [item.id, item]));
        nextItems.forEach((item) => byId.set(item.id, item));
        return Array.from(byId.values());
      });
      setPaginator(response?.paginatorInfo ?? { currentPage: page, hasMorePages: false });
    },
    [execute, pageSize, storeItems]
  );

  useEffect(() => {
    if (skipNextSearchEffect.current) {
      skipNextSearchEffect.current = false;
      return;
    }

    const trimmedSearch = search.trim();

    if (!enabled || trimmedSearch.length < MINIMUM_SEARCH_LENGTH) {
      requestVersion.current += 1;
      if (!preserveItemsOnShortSearch || trimmedSearch.length === 0) {
        setItems([]);
      }
      setActiveSearch("");
      setPaginator({ currentPage: 0, hasMorePages: false });
      return;
    }

    const timer = window.setTimeout(() => {
      loadPage(search.trim(), 1, false);
    }, 300);

    return () => window.clearTimeout(timer);
  }, [enabled, loadPage, preserveItemsOnShortSearch, search]);

  useEffect(() => {
    const missingIds = Array.from(new Set(selectedIds)).filter(
      (id) => !cache[id] && !requestedIds.current.has(id)
    );
    if (!enabled || missingIds.length === 0) return;

    missingIds.forEach((id) => requestedIds.current.add(id));

    Promise.all(
      missingIds.map((id) =>
        execute({ variables: { first: 1, page: 1, search: id } }).then(
          (result) => result.data?.getCompanies?.data ?? []
        )
      )
    )
      .then((results) => storeItems(results.flat()))
      .catch(() => undefined)
      .finally(() => missingIds.forEach((id) => requestedIds.current.delete(id)));
  }, [cache, enabled, execute, selectedIdsKey, storeItems]);

  const resetSearch = useCallback(() => {
    if (search !== "") {
      skipNextSearchEffect.current = true;
    }
    requestVersion.current += 1;
    setSearch("");
    setItems([]);
    setActiveSearch("");
    setPaginator({ currentPage: 0, hasMorePages: false });
  }, [search]);

  const loadMore = useCallback(() => {
    if (loading || !paginator.hasMorePages) {
      return;
    }
    loadPage(activeSearch, paginator.currentPage + 1, true);
  }, [activeSearch, loadPage, loading, paginator]);

  const loadInitialPage = useCallback(() => {
    if (!enabled || loading) {
      return;
    }
    if (activeSearch === "" && items.length > 0) {
      return;
    }
    if (search !== "") {
      skipNextSearchEffect.current = true;
    }
    requestVersion.current += 1;
    setSearch("");
    setItems([]);
    setActiveSearch("");
    setPaginator({ currentPage: 0, hasMorePages: false });
    loadPage("", 1, false);
  }, [activeSearch, enabled, items.length, loadPage, loading, search]);

  const visibleItems = useMemo(() => {
    const selectedItems = selectedIds.map((id) => cache[id]).filter(Boolean) as T[];
    const byId = new Map(selectedItems.map((item) => [item.id, item]));
    items.forEach((item) => byId.set(item.id, item));
    return Array.from(byId.values());
  }, [cache, items, selectedIds]);

  return {
    items: visibleItems,
    loading,
    error,
    search: setSearch,
    loadInitialPage,
    loadMore,
    resetSearch,
    hasMorePages: paginator.hasMorePages,
  } as const;
}
